import { Link } from "react-router-dom";
import { Product } from "@/types/product";
import { Shield, Star, Zap, Heart, ShoppingCart, Eye, Flame, Award, BellRing, MessageCircle, PackageX } from "lucide-react";
import { memo } from "react";
import OptimizedImage from "@/components/OptimizedImage";
import { generateProductUrl } from "@/lib/utils";

const StockBadge = ({ stock }: { stock: number }) => {
  if (stock === 0) return (
    <span className="flex items-center gap-0.5 text-[8px] font-bold text-destructive bg-destructive/10 px-1.5 py-0.5 rounded">
      <PackageX className="w-2.5 h-2.5" /> Out of Stock
    </span>
  );
  if (stock <= 5) return (
    <span className="flex items-center gap-0.5 text-[8px] font-bold text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded">
      <Flame className="w-2.5 h-2.5" /> Only {stock} left
    </span>
  );
  return (
    <span className="flex items-center gap-0.5 text-[8px] font-bold text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded">
      In Stock
    </span>
  );
};

const ProductCard = memo(({ product }: { product: Product }) => {
  const discount = product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;
  const emiPrice = Math.round(product.price / 6);
  const savings = product.originalPrice - product.price;
  const isLowStock = product.stock <= 5 && product.stock > 0;
  const isOutOfStock = product.stock === 0;
  const isBestDeal = discount >= 50;

  return (
    <Link
      to={generateProductUrl(product.id, product.name)}
      className="group block w-full h-full"
    >
      {/* ── MOBILE: Enhanced Flipkart-style Horizontal Card ── */}
      <div className={`flex md:hidden flex-row bg-card rounded-xl p-2.5 gap-3 border-b border-border/40 active:bg-muted/30 transition-all w-full ${isOutOfStock ? 'opacity-80' : ''}`}>
        {/* Left: Image Container */}
        <div className="relative w-[100px] h-[100px] shrink-0 rounded-lg bg-muted/20 flex justify-center items-center overflow-hidden">
          {discount > 0 && (
            <span className="absolute top-1 left-1 bg-primary text-primary-foreground px-1.5 py-0.5 rounded-sm text-[8px] font-black z-10 leading-none shadow-sm">
              -{discount}%
            </span>
          )}
          <OptimizedImage
            src={product.images[0]}
            alt={product.name}
            widths={[200, 400]}
            sizes="100px"
            className={`w-[90%] h-[90%] object-contain mix-blend-multiply dark:mix-blend-normal ${isOutOfStock ? 'grayscale-[40%]' : ''}`}
          />
          {/* Out of Stock — small ribbon at bottom of image */}
          {isOutOfStock && (
            <div className="absolute bottom-0 inset-x-0 bg-destructive/90 py-[3px] z-10">
              <p className="text-center text-destructive-foreground text-[7px] font-black uppercase tracking-wider leading-none">
                Out of Stock
              </p>
            </div>
          )}
        </div>

        {/* Right: Info Section */}
        <div className="flex flex-col flex-1 min-w-0 justify-between py-0.5">
          {/* Header & Title */}
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[9px] font-black text-muted-foreground uppercase tracking-wider">{product.brand}</span>
              {isBestDeal && !isOutOfStock && (
                <span className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded-sm text-[8px] font-black leading-none flex items-center gap-0.5">
                  <Flame className="w-2.5 h-2.5" /> HOT
                </span>
              )}
              {isOutOfStock && (
                <span className="text-[8px] font-bold text-destructive bg-destructive/10 px-1.5 py-0.5 rounded-sm leading-none">
                  Sold Out
                </span>
              )}
            </div>
            <h3 className="text-[13px] font-bold text-foreground leading-[1.3] line-clamp-2">
              {product.name}
            </h3>

            {/* Subtle Spec Pills */}
            {(product.processor || product.ram || product.storage) && (
              <div className="flex flex-wrap items-center text-[10px] text-muted-foreground font-medium mt-1.5 gap-x-1.5 gap-y-0.5">
                {product.processor && <span>{product.processor}</span>}
                {product.processor && (product.ram || product.storage) && <span className="w-0.5 h-0.5 rounded-full bg-border" />}
                {product.ram && <span>{product.ram}</span>}
                {product.ram && product.storage && <span className="w-0.5 h-0.5 rounded-full bg-border" />}
                {product.storage && <span>{product.storage}</span>}
              </div>
            )}
          </div>

          {/* Price */}
          <div className="mt-2.5 flex flex-col gap-0.5">
            <div className="flex items-end gap-1.5 flex-wrap">
              <span className="text-[16px] font-black text-foreground leading-none">
                ₹{product.price.toLocaleString("en-IN")}
              </span>
              {discount > 0 && (
                <>
                  <span className="text-[11px] font-medium text-muted-foreground line-through leading-none mb-[1px]">
                    ₹{product.originalPrice.toLocaleString("en-IN")}
                  </span>
                  <span className="text-[11px] font-bold text-primary leading-none mb-[1px]">
                    {discount}% off
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── DESKTOP: Premium Vertical Card ── */}
      <div className={`hidden md:flex relative flex-col w-full h-full bg-card rounded-2xl border border-border/50 shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden group ${isOutOfStock ? 'opacity-85' : ''}`}>
        {/* Image */}
        <div className="relative w-full aspect-square bg-muted/30 overflow-hidden">
          {discount > 0 && (
            <span className="absolute top-3 left-3 bg-primary text-primary-foreground font-black text-[11px] px-2 py-1 rounded-lg z-10 leading-none shadow-sm">
              -{discount}%
            </span>
          )}
          {isBestDeal && !isOutOfStock && (
            <span className="absolute top-3 right-3 bg-amber-500 text-white font-black text-[10px] px-2 py-1 rounded-lg z-10 leading-none flex items-center gap-1 shadow-sm">
              <Award className="w-3 h-3" /> Best Deal
            </span>
          )}
          {isLowStock && (
            <span className="absolute bottom-3 left-3 bg-destructive/90 text-white font-bold text-[10px] px-2 py-1 rounded-lg z-10 flex items-center gap-1">
              <Flame className="w-3 h-3" /> Only {product.stock} left
            </span>
          )}
          <OptimizedImage
            src={product.images[0]}
            alt={product.name}
            widths={[300, 500, 800]}
            sizes="(max-width: 768px) 50vw, 25vw"
            className={`w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500 ${isOutOfStock ? 'grayscale-[30%]' : ''}`}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />

          {/* Out of Stock — clean bottom ribbon on image */}
          {isOutOfStock && (
            <div className="absolute bottom-0 inset-x-0 z-10 bg-destructive/90 backdrop-blur-sm py-1.5">
              <p className="text-center text-destructive-foreground text-[10px] font-black uppercase tracking-widest leading-none flex items-center justify-center gap-1">
                <PackageX className="w-3 h-3" /> Out of Stock
              </p>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="flex flex-col flex-grow p-4">
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1 leading-none">{product.brand}</span>
          <h3 className="text-[14px] font-bold text-foreground leading-tight mb-1.5 line-clamp-2 min-h-[36px] group-hover:text-primary transition-colors">{product.name}</h3>
          
          {(product.processor || product.ram || product.storage) && (
            <div className="flex flex-wrap gap-1 mb-2">
              {product.processor && <span className="text-[9px] font-bold text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">{product.processor}</span>}
              {product.ram && <span className="text-[9px] font-bold text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">{product.ram}</span>}
              {product.storage && <span className="text-[9px] font-bold text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">{product.storage}</span>}
            </div>
          )}

          <div className="mt-auto space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[20px] font-black text-foreground leading-none">₹{product.price.toLocaleString("en-IN")}</span>
              {product.originalPrice > product.price && (
                <span className="text-[12px] font-medium text-muted-foreground line-through">₹{product.originalPrice.toLocaleString("en-IN")}</span>
              )}
            </div>
            {savings > 0 && (
              <p className="text-[11px] font-bold text-primary leading-none">Save ₹{savings.toLocaleString("en-IN")}</p>
            )}
            <div className="flex items-center justify-between pt-1.5 border-t border-border/40">
              <StockBadge stock={product.stock} />
              <div className="flex items-center gap-1">
                <Shield className="h-3 w-3 text-primary" />
                <span className="text-[10px] font-semibold text-primary">{product.warranty}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
});

ProductCard.displayName = "ProductCard";

export default ProductCard;
